import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const authTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#6C3BFF', light: '#9B6FFF', dark: '#5020E0' },
    secondary: { main: '#8B5CF6' },
    success: { main: '#16A34A', light: '#22C55E' },
    error: { main: '#DC2626', light: '#EF4444' },
    warning: { main: '#D97706', light: '#F59E0B' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: { primary: '#111827', secondary: '#374151', disabled: '#6B7280' },
    divider: '#E5E7EB',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, -apple-system, sans-serif',
    button: { fontWeight: 700, textTransform: 'none' },
    h4: { fontWeight: 800, letterSpacing: '-0.025em' },
    h5: { fontWeight: 800, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.65 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 10,
          transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          '&:active': { transform: 'scale(0.97)' },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF',
            borderRadius: 10,
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '& fieldset': { borderColor: '#E2E8F0', transition: 'border-color 0.2s ease' },
            '&:hover fieldset': { borderColor: '#CBD5E1' },
            '&.Mui-focused fieldset': { borderColor: '#6C3BFF', borderWidth: 2 },
            '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(108,59,255,0.08)' },
          },
          '& .MuiInputBase-input': { color: '#0F172A' },
          '& .MuiInputBase-input::placeholder': { color: '#94A3B8', opacity: 1 },
          '& .MuiInputLabel-root': { color: '#94A3B8' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#6C3BFF' },
          '& .MuiFormHelperText-root': { color: '#94A3B8', marginTop: 4 },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#CBD5E1',
          '&.Mui-checked': { color: '#6C3BFF' },
          '&:hover': { backgroundColor: 'rgba(108,59,255,0.06)' },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 500, fontSize: '0.875rem' },
        standardError: {
          backgroundColor: '#FEF2F2',
          color: '#991B1B',
          border: '1px solid #FECACA',
          '& .MuiAlert-icon': { color: '#DC2626' },
        },
        standardSuccess: {
          backgroundColor: '#F0FDF4',
          color: '#14532D',
          border: '1px solid #BBF7D0',
          '& .MuiAlert-icon': { color: '#16A34A' },
        },
        standardInfo: {
          backgroundColor: '#EFF6FF',
          color: '#1E3A5F',
          border: '1px solid #BFDBFE',
          '& .MuiAlert-icon': { color: '#2563EB' },
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: '#E2E8F0' } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8, transition: 'all 0.15s ease' },
      },
    },
  },
});

const NextGenAuthThemeProvider = ({ children }) => {
  useEffect(() => {
    if (!document.getElementById('ngb-font')) {
      const link = document.createElement('link');
      link.id = 'ngb-font';
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap';
      document.head.insertBefore(link, document.head.firstChild);
    }
  }, []);

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default NextGenAuthThemeProvider;
